import { buildConfigurationPreviewModel, getPreviewAllowedFieldValues, isNumericFieldType, isWholeNumberFieldType } from './configurationAdminUtils';
import { evaluateAdvancedRules } from './advancedRuleEvaluator';

const name = value => String(value || '').trim().toLowerCase();
const bounds = value => value.minValue != null || value.maxValue != null
  ? { min: value.minValue ?? -Infinity, max: value.maxValue ?? Infinity }
  : { min: Number(value.optionValue), max: Number(value.optionValue) };

// Main and Jockey consume the same capacity. Other descriptions retain only
// their own row limits. Capacity is read from the station's Main value rules.
function buildBasePumpRowModels(data, stationId, sectionId, rows, selections = {}, inputs = {}) {
  const quantityField = data.fields.find(field => name(field.fieldName) === 'quantity');
  const descriptionField = data.fields.find(field => name(field.fieldName) === 'pump description');
  const mainValue = data.fieldValues.find(value => value.configFieldId === descriptionField?.id && name(value.optionValue) === 'main');
  const rowSubSections = new Set(data.subSections.filter(sub => sub.sectionId === sectionId).map(sub => sub.id));
  const rowFields = data.fields.filter(field => rowSubSections.has(field.subSectionId));
  const models = rows.map(row => {
    const rowSelections = { ...selections };
    const rowInputs = { ...inputs };
    // Shared station inputs are inherited; pump fields belong exclusively to
    // this row, including fields hidden when the row was first created.
    rowFields.forEach(field => {
      delete rowSelections[field.id];
      delete rowInputs[field.id];
    });
    Object.entries(row.fieldValues).forEach(([id, value]) => {
      if (value.fieldValueId !== undefined) rowSelections[id] = value.fieldValueId;
      if (value.inputValue !== undefined) rowInputs[id] = value.inputValue;
    });
    const model = buildConfigurationPreviewModel(data, stationId, sectionId, rowSelections, rowInputs);
    const fields = model.sections.flatMap(section => section.subSections.flatMap(sub => [...sub.fields, ...(sub.disabledFields || [])]));
    fields.filter(field => isNumericFieldType(field.fieldType) && !field.performance).forEach(field => {
      const raw = rowInputs[field.id] ?? field.inputValue;
      const number = raw === '' || raw === undefined || raw === null ? NaN : Number(raw);
      const ranges = field.values.map(bounds);
      const valid = Number.isFinite(number)
        && (!isWholeNumberFieldType(field.fieldType) || Number.isInteger(number))
        && ranges.some(range => number >= range.min && number <= range.max);
      field.performance = {
        inputValue: raw ?? '',
        error: valid ? '' : 'Value must satisfy the rules for this pump row.',
        hint: `Must be: ${ranges.map(range => range.min === range.max ? range.min : `${range.min}–${range.max}`).join(', ')}`,
      };
    });
    const quantity = fields.find(field => field.id === quantityField?.id);
    const description = fields.find(field => field.id === descriptionField?.id);
    const participates = ['main', 'jockey'].includes(name(description?.selectedValue?.optionValue));
    const rawQuantity = rowInputs[quantityField?.id] ?? quantity?.inputValue;
    const amount = rawQuantity === '' ? NaN : Number(rawQuantity);
    const mainRanges = mainValue && participates ? getPreviewAllowedFieldValues({
      field: quantityField, fieldValues: data.fieldValues, valueRules: data.valueRules,
      stationId, selections: { ...model.effectiveSelections, [descriptionField.id]: mainValue.id },
    }).filter(value => !value.isDisabled).map(bounds) : [];
    const capacity = mainRanges.length ? Math.max(...mainRanges.map(range => range.max)) : Infinity;
    return { key: row.key, model, fields, quantity, rawQuantity, amount, participates, capacity };
  });
  const participants = models.filter(row => row.participates);
  const capacity = Math.min(...participants.map(row => row.capacity));
  const total = participants.reduce((sum, row) => sum + (Number.isFinite(row.amount) ? row.amount : 0), 0);
  models.forEach(row => {
    if (!row.quantity) return;
    const enabledValues = row.quantity.values.filter(value => !value.isDisabled);
    const ranges = (enabledValues.length ? enabledValues : row.quantity.values).map(bounds);
    const remaining = row.participates ? capacity - (total - (Number.isFinite(row.amount) ? row.amount : 0)) : Infinity;
    const allowed = ranges.map(range => ({ min: range.min, max: Math.min(range.max, remaining) })).filter(range => range.max >= range.min);
    const valid = Number.isInteger(row.amount) && allowed.some(range => row.amount >= range.min && row.amount <= range.max);
    row.quantity.performance = {
      inputValue: row.rawQuantity ?? '',
      allowedRanges: allowed,
      fixedInputValue: enabledValues.length > 0 && ranges.length === 1 && ranges[0].min === ranges[0].max ? ranges[0].min : undefined,
      error: valid ? '' : 'Quantity exceeds the row allowance or the shared Main/Jockey capacity.',
      hint: allowed.length ? `Must be: ${allowed.map(range => range.min === range.max ? range.min : `${range.min}–${range.max}`).join(', ')}` : 'No quantity available within the shared limit',
    };
  });
  return { rows: models, total, capacity, isValid: models.every(row => row.fields.every(field => !field.performance?.error) && row.model.performance?.isValid !== false) };
}

export const getFixedPumpQuantityUpdates = (model) => (model?.rows || []).flatMap(row => {
  const fixed = row.quantity?.performance?.fixedInputValue;
  return fixed !== undefined && String(row.rawQuantity) !== String(fixed)
    ? [{ rowKey: row.key, fieldId: row.quantity.id, inputValue: String(fixed) }]
    : [];
});

export function buildPumpRowModels(data, stationId, sectionId, rows, selections = {}, inputs = {}) {
  const base = buildBasePumpRowModels(data, stationId, sectionId, rows, selections, inputs);
  const advanced = evaluateAdvancedRules({
    rules: data.advancedRules || [], stationId,
    rows: base.rows.map(row => ({ key: row.key, selections: row.model.effectiveSelections, inputs: row.model.effectiveInputs })),
    canSetValue: (row, fieldId, valueId) => {
      const field = base.rows.find(item => item.key === row.key)?.fields.find(item => item.id === fieldId);
      return field && !field.isDisabled && !field.isSelectedValueDisabled && field.values.some(value => value.id === valueId && !value.isDisabled);
    },
  });
  const adjustedRows = rows.map(row => ({ ...row, fieldValues: { ...row.fieldValues } }));
  advanced.updates.forEach(update => {
    const row = adjustedRows.find(item => item.key === update.rowKey);
    row.fieldValues[update.fieldId] = { ...row.fieldValues[update.fieldId], fieldValueId: update.fieldValueId };
  });
  const result = advanced.updates.length ? buildBasePumpRowModels(data, stationId, sectionId, adjustedRows, selections, inputs) : base;
  return { ...result, advanced, isValid: result.isValid && advanced.isValid };
}
